import { useMemo } from "react";

import { type ChartDataset } from "chart.js";
import { compact, flatten, keyBy, orderBy } from "lodash";
import moment from "moment";

import { useUser } from "@components/user-context";
import { DesignElementManager } from "@hooks/use-design-element-manager";
import { useYesterday } from "@hooks/use-yesterday";
import { type FetchLiveForecastTrackingData_me_mmm_performanceTracking_liveForecast } from "@north-beam/nb-client-side";
import { ISO_DATE_FORMAT } from "@north-beam/nb-common";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { usePerformanceTrackingChartView } from "../../../hooks/use-chart-state";
import { useAllSelectedRowIds } from "../../../table/contexts";
import { accumulateYValues } from "../../chart-accumulator";
import {
  ACTUAL_COLOR,
  type ACTUAL_ID,
  BUDGETED_COLOR,
  type BUDGETED_ID,
  LIVE_FORECAST_BORDER_DASH,
  LIVE_FORECAST_COLOR,
  LIVE_FORECAST_ID,
} from "../const";

type SpendDataAPIDatum = {
  key: string;
  breakdowns: Record<string, string>;
  actualSpend: number;
  budgetedSpend: number;
  dataColumnName: string;
  dailyMetrics: {
    date: string;
    actual: number | null;
    budgeted: number | null;
  }[];
};

const PLATFORM_KEY_NAME = "Platform (Northbeam)";

type ChartJSDatum = {
  x: number;
  y: number | null;
};

export type PerformanceTrackingDataset = ChartDataset<
  "line",
  ChartJSDatum[]
> & {
  metadata: {
    breakdowns: Record<string, string>;
    color: string;
    label: string;
    lineType: typeof ACTUAL_ID | typeof BUDGETED_ID | typeof LIVE_FORECAST_ID;
    borderDash?: number[];
  };
  id: string;
};

type DataColumnMapping = {
  dataColumn: string;
  key: string;
};

type DataColumnMappings = Record<string, DataColumnMapping>;

export function useBuildSpendDataset({
  data,
  datasetVisibility,
}: {
  data: SpendDataAPIDatum[];
  datasetVisibility: Record<string, boolean>;
}) {
  const { user } = useUser();

  const { selectedCycle } = useSelectedCycle();
  const [chartView] = usePerformanceTrackingChartView();

  const [allSelectedRowIds] = useAllSelectedRowIds();

  const datasets: PerformanceTrackingDataset[] = [];

  const allSelectedRowIdsSet = new Set(allSelectedRowIds);

  const datasetId = "performance-tracking";

  const yesterday = useYesterday();

  const dataColumnMappings: DataColumnMappings = keyBy(
    data.map((d) => {
      return {
        dataColumn: d.dataColumnName,
        key: d.key,
      };
    }),
    "dataColumn",
  );

  // Do spend dataset building
  if (chartView === "split") {
    // get all the breakdown combinations
    const spendSplitViewDatasets = compact(
      data.map((breakdownData, rowIndex) => {
        // Don't show the data if it's not selected
        if (!allSelectedRowIdsSet.has(breakdownData.key)) {
          return null;
        }

        const actualSpendDataset: PerformanceTrackingDataset = {
          data: [],
          metadata: {
            breakdowns: breakdownData.breakdowns,
            color: getColorForIndexOrPlatform(
              rowIndex,
              breakdownData.breakdowns,
            ),
            lineType: "Actual",
            label: selectedCycle.breakdowns
              .map(
                (breakdown) =>
                  breakdownData.breakdowns[breakdown] ?? "(not set)",
              )
              .join(" / "),
          },
          hidden: !datasetVisibility["Actual"],
          id: datasetId,
        };
        const budgetedSpendDataset: PerformanceTrackingDataset = {
          data: [],
          metadata: {
            breakdowns: breakdownData.breakdowns,
            color: getColorForIndexOrPlatform(
              rowIndex,
              breakdownData.breakdowns,
            ),
            lineType: "Budgeted",
            label: selectedCycle.breakdowns
              .map(
                (breakdown) =>
                  breakdownData.breakdowns[breakdown] ?? "(not set)",
              )
              .join(" / "),
            borderDash: [4, 5],
          },
          hidden: !datasetVisibility["Budgeted"],
          id: datasetId,
        };
        breakdownData.dailyMetrics.forEach((datapoint, index) => {
          if (moment(datapoint.date).isSameOrBefore(yesterday)) {
            actualSpendDataset.data.push({
              x: index,
              y: datapoint.actual,
            });
          }

          budgetedSpendDataset.data.push({
            x: index,
            y: datapoint.budgeted,
          });
        });
        actualSpendDataset.data = accumulateYValues(actualSpendDataset.data);
        budgetedSpendDataset.data = accumulateYValues(
          budgetedSpendDataset.data,
        );

        return [actualSpendDataset, budgetedSpendDataset];
      }),
    );

    datasets.push(...flatten(spendSplitViewDatasets));
  } else {
    /* aggregate all the data for spend
         we default to primary when in aggregate mode */
    const actualSpendDataset: PerformanceTrackingDataset = {
      data: [],
      metadata: {
        breakdowns: {},
        color: ACTUAL_COLOR,
        label: "Actual",
        lineType: "Actual",
      },
      hidden: !datasetVisibility["Actual"],
      id: "Actual",
    };
    const budgetedSpendDataset: PerformanceTrackingDataset = {
      data: [],
      metadata: {
        breakdowns: {},
        color: BUDGETED_COLOR,
        label: "Budgeted",
        lineType: "Budgeted",
      },
      hidden: !datasetVisibility["Budgeted"],
      id: "Budgeted",
    };
    const actualAccumulator: { [week: number]: number } = {};
    const bugetedAccumulator: { [week: number]: number } = {};

    // aggregate the data points
    data.forEach((breakdownData) => {
      breakdownData.dailyMetrics.forEach((datapoint, index) => {
        // Don't sum the data if it's not selected
        if (
          allSelectedRowIdsSet.size === 0 ||
          !allSelectedRowIdsSet.has(breakdownData.key)
        ) {
          return;
        }

        if (moment(datapoint.date).isSameOrBefore(yesterday)) {
          actualAccumulator[index] =
            (actualAccumulator[index] || 0) + (datapoint.actual ?? 0);
        }
        bugetedAccumulator[index] =
          (bugetedAccumulator[index] || 0) + (datapoint.budgeted ?? 0);
      });
    });

    // generate the dataset
    const actualData = accumulateYValues(reduceDataset(actualAccumulator));
    actualSpendDataset.data = actualData;

    budgetedSpendDataset.data = accumulateYValues(
      reduceDataset(bugetedAccumulator),
    );

    // add the dataset
    datasets.push(actualSpendDataset, budgetedSpendDataset);
  }

  const dateLabels =
    data[0]?.dailyMetrics.map(
      ({ date }) => `${moment(date).format("MMM DD, YYYY")}`,
    ) ?? [];

  return {
    ...styleChartDataset({ labels: dateLabels, datasets }),
    dataColumnMappings,
  };
}

function reduceDataset(acc: { [week: number]: number }): ChartJSDatum[] {
  const data: ChartJSDatum[] = Object.keys(acc).map((wk) => {
    const week = Number(wk);
    return {
      y: acc[week],
      x: week,
    };
  });
  return orderBy(data, (i) => i.x, "asc");
}

export function styleChartDataset(data: {
  labels: string[];
  datasets: PerformanceTrackingDataset[];
}): {
  labels: string[];
  datasets: ChartDataset<"line", ChartJSDatum[]>[];
  dataColumnMappings: DataColumnMappings;
} {
  const datasets = [];

  for (const [i, dataset] of data.datasets.entries()) {
    const singleDataPoint = dataset.data.length === 1;
    const { color, borderDash } = dataset.metadata;

    const backgroundColor = color;
    const borderWidth = 3;
    const borderColor = color;

    datasets.push({
      ...dataset,
      pointRadius: singleDataPoint ? 3 : 0,
      animation: false,
      borderColor,
      hoverBackgroundColor: backgroundColor,
      hoverBorderColor: borderColor,
      borderWidth,
      borderDash,
      labelColor: color,
    });
  }

  return { ...data, datasets };
}

const designElementManager = new DesignElementManager();

export function getColorForIndexOrPlatform(
  index: number,
  breakdowns: Record<string, string> | Record<string, string>[],
) {
  let color = designElementManager.getColorForIndex(index);

  if (Object.keys(breakdowns).length === 1 && PLATFORM_KEY_NAME in breakdowns) {
    const platform = breakdowns[PLATFORM_KEY_NAME];
    color = designElementManager.getDesignElementColor(platform);
  }

  return color;
}

export function useBuildLiveForecastSpendDataset({
  data,
  datasetVisibility,
  dataColumnMappings,
}: {
  data: FetchLiveForecastTrackingData_me_mmm_performanceTracking_liveForecast["data"];
  datasetVisibility: Record<string, boolean>;
  dataColumnMappings: DataColumnMappings;
}) {
  const { user } = useUser();

  const dateLabels =
    data.map(({ date }) => `${moment(date).format("MMM DD, YYYY")}`) ?? [];

  const yesterday = useYesterday();
  const [allSelectedRowIds] = useAllSelectedRowIds();

  const { forecastAggSpendData } = useMemo(() => {
    const allSelectedRowIdsSet = new Set(allSelectedRowIds);
    const _forecastAggSpendData: {
      y: number | null;
      x: number;
      nullify?: boolean;
    }[] = [];

    data.forEach((row, index) => {
      let spendValue = 0;
      row.spend_datacolumns.forEach((spend) => {
        const dataColumnMapping = dataColumnMappings[spend.key];

        if (!allSelectedRowIdsSet.has(dataColumnMapping?.key)) {
          return;
        }
        spendValue += spend.value;
      });

      _forecastAggSpendData.push({
        y: spendValue,
        x: index,
        nullify: moment.tz(row.date, user.timezone).isBefore(yesterday),
      });
    });

    return {
      forecastAggSpendData: accumulateYValues(_forecastAggSpendData),
    };
  }, [allSelectedRowIds, data, dataColumnMappings, user.timezone, yesterday]);

  return {
    dateLabels,
    datasets: [
      {
        label: LIVE_FORECAST_ID,
        id: LIVE_FORECAST_ID,
        data: forecastAggSpendData,
        metadata: {
          breakdowns: {},
          color: LIVE_FORECAST_COLOR,
          label: LIVE_FORECAST_ID,
          lineType: LIVE_FORECAST_ID,
          borderDash: LIVE_FORECAST_BORDER_DASH,
        },
        borderColor: LIVE_FORECAST_COLOR,
        backgroundColor: LIVE_FORECAST_COLOR,
        yAxisID: "y",
        hidden: !datasetVisibility[LIVE_FORECAST_ID],
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.5,
        animation: false,
        borderDash: LIVE_FORECAST_BORDER_DASH,
      },
    ],
  };
}
